// Screen 6: Project Boards — kanban-style research workspace.

import { useAuth } from '../src/lib/auth.jsx';
import { usePapers } from '../src/lib/use-papers.js';

const BOARD_STATUSES = [
  { id: 'exploring',    color: 'var(--acc-violet)', labelKo: '탐색', labelEn: 'Exploring' },
  { id: 'implementing', color: 'var(--acc-cyan)',   labelKo: '구현 중', labelEn: 'Implementing' },
  { id: 'done',         color: 'var(--ok)',         labelKo: '완료', labelEn: 'Done' },
  { id: 'archived',     color: 'var(--fg-4)',       labelKo: '보관', labelEn: 'Archived' },
];

async function setPaperStatus(paperId, status) {
  try {
    await fetch(`/api/papers/${paperId}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ status }),
    });
  } catch {}
}

function ProjectBoardScreen() {
  const { t, lang } = useT();
  const auth = useAuth();
  const { papers, refresh } = usePapers();
  const proj = PROJECT_BOARD;
  // Translate column labels
  const colTitleMap = {
    'collected': t('col.collected'),
    'reading': t('col.reading'),
    'important': t('col.important'),
    'repro': t('col.repro'),
    'implementing': t('col.implementing'),
    'used': t('col.used'),
  };
  return (
    <div className="pp pp-screen" data-screen-label="06 Project boards">
      <Sidebar active="projects"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.projects'), lang === 'ko' ? '서버랙 디지털 트윈' : 'Server-rack digital twin']}/>}
          search="Ask AI about this project…"
        />

        {/* project header */}
        <div className="pp-proj-header">
          <div className="col gap-2" style={{ flex: 1, minWidth: 0 }}>
            <div className="row gap-3" style={{ alignItems: 'baseline' }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: '#8896FF', boxShadow: '0 0 8px #8896FF' }}/>
              <span className="serif" style={{ fontSize: 24, letterSpacing: '-0.012em' }}>{lang === 'ko' ? '서버랙 디지털 트윈' : 'Server-rack digital twin'}</span>
              <span className="chip ghost mono">{t('proj.triaged')}</span>
              <span className="chip ok" style={{ height: 22 }}><I name="check" size={10}/> {t('proj.ontrack')}</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-1)', maxWidth: 720 }}>{proj.description}</div>
            <div className="row gap-3" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
              <span className="mono">{t('proj.started')}</span>
              <span>·</span>
              <span><span className="mono" style={{ color: 'var(--acc-cyan)' }}>3</span> {t('proj.collab')}</span>
              <span>·</span>
              <span><span className="mono" style={{ color: 'var(--acc-indigo)' }}>14</span> {t('proj.newpapers')}</span>
              <span>·</span>
              <span><span className="mono" style={{ color: 'var(--acc-violet)' }}>5</span> {t('proj.benchref')}</span>
            </div>
          </div>
          <div className="row gap-2">
            <button className="btn sm ghost"><I name="plus" size={12}/> {t('proj.add')}</button>
            <button className="btn sm ghost"><I name="sparkles" size={12}/> {t('proj.askai')}</button>
            <button className="btn sm primary"><I name="zap" size={12}/> {t('proj.generate')}</button>
          </div>
        </div>

        {/* project meta strip */}
        <div className="pp-proj-strip">
          <ProjStripBlock title={t('proj.direction')} tone="indigo">
            <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.5 }}>
              {t('proj.direction.body')}
            </div>
          </ProjStripBlock>
          <ProjStripBlock title={t('proj.impl')} tone="cyan" count="3">
            <div className="col gap-1" style={{ fontSize: 11.5 }}>
              <div className="row gap-2"><span className="mono" style={{ color: 'var(--ok)' }}>★</span> GS-DiT · <span style={{ color: 'var(--fg-2)' }}>{t('proj.impl.repro')}</span></div>
              <div className="row gap-2"><span className="mono" style={{ color: 'var(--acc-cyan)' }}>·</span> SV-3DGS · <span style={{ color: 'var(--fg-2)' }}>{t('proj.impl.inp')}</span></div>
              <div className="row gap-2"><span className="mono" style={{ color: 'var(--fg-3)' }}>·</span> 4DGS-Cache · <span style={{ color: 'var(--fg-2)' }}>{t('proj.impl.blocked')}</span></div>
            </div>
          </ProjStripBlock>
          <ProjStripBlock title={t('proj.targets')} tone="violet" count="5">
            <div className="row gap-3" style={{ fontSize: 11 }}>
              <span><span className="mono" style={{ color: 'var(--fg-0)', fontSize: 14 }}>31.7</span> <span style={{ color: 'var(--fg-3)' }}>{t('proj.psnr')}</span></span>
              <span><span className="mono" style={{ color: 'var(--fg-0)', fontSize: 14 }}>30 Hz</span> <span style={{ color: 'var(--fg-3)' }}>{t('proj.interactive')}</span></span>
              <span><span className="mono" style={{ color: 'var(--fg-0)', fontSize: 14 }}>&lt;50 cm</span> <span style={{ color: 'var(--fg-3)' }}>{t('proj.pose')}</span></span>
            </div>
          </ProjStripBlock>
          <ProjStripBlock title={t('proj.figs')} tone="warn" count="8">
            <div className="row gap-1">
              {[1,2,3,4].map(i => (
                <div key={i} style={{ width: 38, height: 28, borderRadius: 4, border: '1px solid var(--border-2)', background: `radial-gradient(circle at ${20+i*15}% ${40}%, rgba(${i%2?'136,150,255':'180,155,255'},0.45), transparent 65%), #14172A` }}/>
              ))}
              <div style={{ width: 38, height: 28, borderRadius: 4, border: '1px dashed var(--border-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--fg-3)' }} className="mono">+4</div>
            </div>
          </ProjStripBlock>
        </div>

        <div className="pp-content" style={{ overflow: 'hidden' }}>
          <div className="pp-board scroll">
            {(() => {
              const userOwned = papers.filter((p) => p?._db);
              if (userOwned.length === 0) {
                // No imported papers yet → keep the seeded demo board.
                return <>
                  {proj.columns.map(col => <BoardColumn key={col.id} col={{...col, label: colTitleMap[col.id] || col.label}}/>)}
                  <BoardColumn col={{ id: 'archived', label: t('col.archived'), color: 'var(--fg-4)', cards: [] }}/>
                </>;
              }
              return BOARD_STATUSES.map((status) => (
                <PaperStatusColumn
                  key={status.id}
                  status={status}
                  papers={userOwned.filter((p) => (p.status || 'exploring') === status.id)}
                  onMove={async (paperId, nextStatus) => {
                    await setPaperStatus(paperId, nextStatus);
                    refresh();
                  }}
                  lang={lang}
                />
              ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function PaperStatusColumn({ status, papers, onMove, lang }) {
  return (
    <div className="pp-board-col">
      <div className="pp-board-col-head">
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <div style={{ width: 5, height: 5, borderRadius: 999, background: status.color, boxShadow: `0 0 6px ${status.color}` }}/>
          <span style={{ fontSize: 11, color: 'var(--fg-0)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {lang === 'ko' ? status.labelKo : status.labelEn}
          </span>
          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{papers.length}</span>
        </div>
      </div>
      <div className="pp-board-col-body">
        {papers.length === 0 && (
          <div className="pp-board-empty">
            <I name="archive" size={16} style={{ color: 'var(--fg-4)' }}/>
            <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
              {lang === 'ko' ? '비어있음' : 'Empty'}
            </span>
          </div>
        )}
        {papers.map((p) => (
          <DbBoardCard key={p.id} paper={p} status={status} onMove={onMove} lang={lang}/>
        ))}
      </div>
    </div>
  );
}

function DbBoardCard({ paper, status, onMove, lang }) {
  const nav = React.useContext(NavContext);
  const meta = paper._meta ?? {};
  const venue = paper.venue || meta.primary_category || '';
  return (
    <div className="pp-board-card"
         style={{ cursor: 'pointer' }}
         onClick={() => nav.navigate?.('detail', paper?.id ? { id: paper.id } : undefined)}>
      <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        {venue && <span className="chip accent" style={{ height: 18, padding: '0 6px', fontSize: 10 }}>{venue}</span>}
        {paper.arxiv && <span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-3)' }}>arXiv {paper.arxiv}</span>}
      </div>
      <div className="serif" style={{ fontSize: 13.5, lineHeight: 1.25, marginTop: 6 }}>{paper.title}</div>
      {paper.summary && (
        <div style={{ fontSize: 11, color: 'var(--fg-2)', marginTop: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {paper.summary}
        </div>
      )}
      <div className="row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-1)', justifyContent: 'space-between', alignItems: 'center' }}>
        <select
          value={status.id}
          onChange={(e) => onMove(paper.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--bg-2)', color: 'var(--fg-1)',
            border: '1px solid var(--border-1)', borderRadius: 4,
            fontSize: 10, padding: '2px 4px', cursor: 'pointer',
          }}
        >
          {BOARD_STATUSES.map((s) => (
            <option key={s.id} value={s.id}>{lang === 'ko' ? s.labelKo : s.labelEn}</option>
          ))}
        </select>
        {meta.html_url && (
          <a href={meta.html_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
             style={{ fontSize: 10, color: 'var(--fg-3)' }}>
            <I name="external" size={10}/> arxiv
          </a>
        )}
      </div>
    </div>
  );
}

function ProjStripBlock({ title, tone, count, children }) {
  const map = { indigo: 'var(--acc-indigo)', cyan: 'var(--acc-cyan)', violet: 'var(--acc-violet)', warn: 'var(--warn)' };
  const c = map[tone];
  return (
    <div className="pp-proj-strip-block">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <div style={{ width: 3, height: 10, background: c, borderRadius: 1, boxShadow: `0 0 6px ${c}` }}/>
          <span style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--fg-2)' }}>{title}</span>
        </div>
        {count && <span className="mono" style={{ fontSize: 10, color: c }}>{count}</span>}
      </div>
      {children}
    </div>
  );
}

function BoardColumn({ col }) {
  const { t } = useT();
  return (
    <div className="pp-board-col">
      <div className="pp-board-col-head">
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <div style={{ width: 5, height: 5, borderRadius: 999, background: col.color, boxShadow: `0 0 6px ${col.color}` }}/>
          <span style={{ fontSize: 11, color: 'var(--fg-0)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{col.label}</span>
          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{col.cards.length}</span>
        </div>
        <div className="row gap-1">
          <button className="iconbtn" style={{ width: 22, height: 22 }}><I name="plus" size={12}/></button>
          <button className="iconbtn" style={{ width: 22, height: 22 }}><I name="more" size={12}/></button>
        </div>
      </div>
      <div className="pp-board-col-body">
        {col.cards.length === 0 && (
          <div className="pp-board-empty">
            <I name="archive" size={16} style={{ color: 'var(--fg-4)' }}/>
            <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('col.empty')}</span>
          </div>
        )}
        {col.cards.map((c, i) => {
          const paper = PAPERS.find(p => p.id === c.pid);
          if (!paper) return null;
          return <BoardCard key={i} card={c} paper={paper}/>;
        })}
      </div>
    </div>
  );
}

function BoardCard({ card, paper }) {
  const { t } = useT();
  const nav = React.useContext(NavContext);
  return (
    <div className="pp-board-card" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}>
      <div className="row gap-2" style={{ alignItems: 'baseline' }}>
        <span className="chip accent" style={{ height: 18, padding: '0 6px', fontSize: 10 }}>{paper.venue.replace(/ \d+/, '')} ’{paper.venue.match(/\d{2}/)?.[0]}</span>
        {card.code && <span className="chip ok" style={{ height: 18, padding: '0 5px' }}><I name="github" size={9}/></span>}
        {card.bench && <span className="chip cyan mono" style={{ height: 18 }}>{card.bench}</span>}
      </div>
      <div className="serif" style={{ fontSize: 13.5, lineHeight: 1.25, marginTop: 6 }}>{paper.title}</div>
      <div style={{ fontSize: 11, color: 'var(--fg-2)', marginTop: 4 }}>{card.value}</div>
      <div className="row gap-2" style={{ marginTop: 8, flexWrap: 'wrap' }}>
        {paper.tags.slice(0, 2).map(t => <span key={t} className="tag indigo" style={{ height: 16, fontSize: 9.5, padding: '0 5px' }}>{t}</span>)}
      </div>
      <div className="row" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-1)', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="row gap-2">
          <span title="Implementation difficulty" className="row gap-1">
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{
                width: 4, height: 6, borderRadius: 1,
                background: i <= card.diff ? (card.diff >= 4 ? 'var(--warn)' : 'var(--acc-cyan)') : 'rgba(255,255,255,0.10)'
              }}/>
            ))}
          </span>
          <span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-3)' }}>{t('card.diff')} {card.diff}/5</span>
        </div>
        <div className="row gap-2" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
          {card.note && <span className="row gap-1"><I name="comment" size={10}/>{card.note}</span>}
          <Avatars names={paper.authors.slice(0, 2)} size={14}/>
        </div>
      </div>
    </div>
  );
}

if (!document.getElementById('pp-board-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-board-styles';
  s.textContent = `
  .pp-proj-header {
    padding: 22px 26px 14px;
    display: flex; gap: 24px;
    align-items: flex-start; justify-content: space-between;
    border-bottom: 1px solid var(--border-1);
    position: relative;
  }
  .pp-proj-strip {
    display: grid; grid-template-columns: 1.6fr 1fr 1fr 1fr; gap: 1px;
    background: var(--border-1);
    border-bottom: 1px solid var(--border-1);
  }
  .pp-proj-strip-block {
    padding: 12px 18px;
    background: var(--bg-1);
    display: flex; flex-direction: column;
  }

  .pp-board {
    flex: 1;
    display: flex; gap: 12px;
    padding: 18px 18px 24px;
    overflow-x: auto;
    overflow-y: hidden;
    background: var(--bg-0);
  }
  .pp-board-col {
    flex: 0 0 264px;
    display: flex; flex-direction: column;
    background: var(--bg-1);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    max-height: 100%;
    min-height: 0;
  }
  .pp-board-col-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid var(--border-1);
  }
  .pp-board-col-body {
    display: flex; flex-direction: column; gap: 10px;
    padding: 12px;
    overflow-y: auto;
    flex: 1;
  }
  .pp-board-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 8px;
    padding: 10px 12px;
    cursor: grab;
    transition: border-color .12s, transform .12s;
  }
  .pp-board-card:hover { border-color: var(--border-3); }
  .pp-board-empty {
    padding: 24px 12px;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    color: var(--fg-3);
    border: 1px dashed var(--border-1);
    border-radius: 8px;
  }
  `;
  document.head.appendChild(s);
}

export { ProjectBoardScreen };
